"use client"

import { useState, useEffect } from "react"
import axios from "axios"
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table"
import { Button } from "../../ui/button"
import { Badge } from "../../ui/badge"
import { Pagination } from "../../common/pagination-dynamic"
import { Eye, Pencil, Ban, BellRing } from "lucide-react"
import { LeaveReviewModalEmp } from "../../modals/leave-review-modal-emp"
import { CancelLeaveConfirmationModal } from "../../modals/cancel-leave-confirmation-modal"
import { useToast } from "../../hooks/use-toast"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { AddLeaveForm } from "./add-leave-form";


interface LeaveDayDetail {
  date: string; // YYYY-MM-DD
  type: "Full Day" | "First-Half" | "Second-Half";
}
const normalizeDayType = (value: any): LeaveDayDetail["type"] => {
  if (typeof value !== "string") return "Full Day";
  const lower = value.toLowerCase();
  if (lower.includes("first")) return "First-Half";
  if (lower.includes("second")) return "Second-Half";
  return "Full Day";
};
const normalizeDayDate = (value: any): string | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (!isNaN(parsed.getTime())) return formatDateToYMD(parsed);
  if (typeof value === "string") {
    const parts = value.split("-");
    if (parts.length === 3) {
      const [y, m, d] = parts.map(Number);
      if (y && m && d) {
        return formatDateToYMD(new Date(y, m - 1, d));
      }
    }
  }
  return null;
};
const formatDateToYMD = (date: Date | string) => {
  const d = new Date(date); // convert string → Date if needed
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

function generateLeaveDayDetails(start: string, end: string, totalDays: number): LeaveDayDetail[] {
  const startDate = new Date(start);
  const endDate = new Date(end);

  const daysArray: LeaveDayDetail[] = [];
  let current = new Date(startDate);

  while (current <= endDate) {
    const dayType: LeaveDayDetail["type"] =
      totalDays % 1 !== 0 ? (current.getTime() === startDate.getTime() ? "First-Half" : "Second-Half") : "Full Day";

    if (current.getDay() !== 0 && current.getDay() !== 6) { // skip weekends
      

daysArray.push({
  date: formatDateToYMD(current), // ✅ local date, no UTC conversion
  type: dayType,
});
    }

    current.setDate(current.getDate() + 1);
  }

  return daysArray;
}



export interface LeaveRecord {
  id: number;            // ← must exist
  type: string;
  startDate: string;
  endDate: string;
  days: number;
  reason: string;
  status: string;
  appliedDate: string;
  hrName?: string;
  managerName?: string;
  hrResponse?: string | null;
  managerResponse?: string | null;
  hrComment?: string | null;
  managerComment?: string | null;
  isCancelled?: boolean;
  leaveDayDetails?: LeaveDayDetail[];
}



const getStatusBadge = (status: string) => {
  switch (status.toLowerCase()) {
    case "approved":
      return (
        <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
          Approved
        </Badge>
      )
    case "pending manager approval":
    case "pending_manager_approval":
      return (
        <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">
          Pending Manager Approval
        </Badge>
      )
    case "pending hr approval":
    case "pending_hr_approval":
      return (
        <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
          Pending HR Approval
        </Badge>
      )
    case "cancelled":
      return (
        <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">
          Cancelled
        </Badge>
      )
    case "rejected_by_manager":
    case "rejected by manager":
      return (
        <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
          Rejected by Manager
        </Badge>
      )
    case "rejected_by_hr":
    case "rejected by hr":
      return (
        <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
          Rejected by HR
        </Badge>
      )
    default:
      return (
        <Badge className="bg-gray-100 text-gray-600 hover:bg-gray-100">
          N/A
        </Badge>
      )
  }
}
interface LeaveRecordWithDetails {
  id: number;
  type: string;
  startDate: Date; // Date object for form
  endDate: Date; // Date object for form
  days: number;
  reason: string;
  status: string;
  appliedDate: string;
  leaveDayDetails: LeaveDayDetail[];
  requestID: number;
  managerResponseOn?: string | null;
  hrResponseOn?: string | null;
  hrName?: string;
  managerName?: string;
  hrResponse?: string | null;
  managerResponse?: string | null;
  hrComment?: string | null;
  managerComment?: string | null;
  isCancelled?: boolean;
}


export function ViewLeaveHistory() {
  const [pageSize, setPageSize] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false)
  const [selectedLeave, setSelectedLeave] = useState<LeaveRecordWithDetails | null>(null)
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const [leaveToCancelId, setLeaveToCancelId] = useState<number | null>(null)
  const [leaveToCancel, setLeaveToCancel] = useState<LeaveRecord | null>(null)
  const [leaveHistory, setLeaveHistory] = useState<LeaveRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const { toast } = useToast()

  // ✅ Move these inside the component
  const [isAddLeaveModalOpen, setIsAddLeaveModalOpen] = useState(false)
  const [leaveToEdit, setLeaveToEdit] = useState<LeaveRecordWithDetails | null>(null)

const handleEditLeave = (leave: LeaveRecord) => {
    // Convert string dates to Date objects for the form
    // Parse YYYY-MM-DD format properly to avoid timezone issues
    const parseDateString = (dateStr: string): Date => {
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        // Create date in local timezone to avoid UTC conversion issues
        return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      }
      return new Date(dateStr);
    };

    const startDate = parseDateString(leave.startDate);
    const endDate = parseDateString(leave.endDate);

    const leaveDayDetails =
      leave.leaveDayDetails && leave.leaveDayDetails.length > 0
        ? leave.leaveDayDetails.filter((day: any) => !day.isCancelled && !day.IsCancelled) // Filter out cancelled days
        : generateLeaveDayDetails(leave.startDate, leave.endDate, leave.days);

    setLeaveToEdit({
      ...leave,
      startDate: startDate,
      endDate: endDate,
      leaveDayDetails,
      requestID: leave.id,
    });
    setIsAddLeaveModalOpen(true);
  }

  const fetchLeaveHistory = async () => {
      try {
      const response = await axios.get(process.env.REACT_APP_API_URL +"/api/Leave/GetAllLRApprovalWorkflows", {
        withCredentials: true, // ✅ include credentials (cookies, tokens)
      })

        // Map API response to LeaveRecord structure
        const formattedData: LeaveRecord[] = response.data.map((item: any) => {
          // Convert dates to YYYY-MM-DD format strings
          const startDateStr = item.leaveStartDate 
            ? (typeof item.leaveStartDate === 'string' 
                ? item.leaveStartDate.split('T')[0] 
                : formatDateToYMD(new Date(item.leaveStartDate)))
            : '';
          const endDateStr = item.leaveEndDate 
            ? (typeof item.leaveEndDate === 'string' 
                ? item.leaveEndDate.split('T')[0] 
                : formatDateToYMD(new Date(item.leaveEndDate)))
            : '';

          return {
            id: item.requestID,
            type: item.leaveName,
            startDate: startDateStr,
            endDate: endDateStr,
            days: item.leaveDayDetails
              ?.filter((day: any) => !day.isCancelled && !day.IsCancelled) // Exclude cancelled days
              ?.reduce((sum: number, day: any) => {
                if (day.dayType === "First-Half" || day.dayType === "Second-Half") return sum + 0.5;
                return sum + 1; // Full day
              }, 0) || 0,
            reason: item.reason,
            status: item.statusName
              .toLowerCase()
              .replace(/\s+/g, "_") as LeaveRecord["status"],
            appliedDate: new Date(item.createdOn).toLocaleDateString(),
            leaveDayDetails: (item.leaveDayDetails || [])
              .filter((detail: any) => !detail.isCancelled && !detail.IsCancelled) // Filter out cancelled days
              .map((detail: any) => {
                const normalizedDate = normalizeDayDate(detail.date || detail.leaveDate || detail.leaveDay);
                if (!normalizedDate) return null;
                return {
                  date: normalizedDate,
                  type: normalizeDayType(detail.dayType),
                };
              })
              .filter((detail: LeaveDayDetail | null): detail is LeaveDayDetail => Boolean(detail)),
          };
        })

        setLeaveHistory(formattedData)
      } catch (error) {
        console.error("Error fetching leave history:", error)
      } finally {
        setLoading(false)
      }
    }

  // 🔹 Fetch data from API
  useEffect(() => {
    

    fetchLeaveHistory()
  }, [])

  const handleViewDetails = async (leave: LeaveRecord) => {
  setSelectedLeave(null);

  try {
    const response = await axios.get(
      process.env.REACT_APP_API_URL + `/api/Leave/GetAllLRApprovalWorkflows`,
      { withCredentials: true }
    );

    const item = response.data.find((wf: any) => wf.requestID === leave.id);
    if (!item) throw new Error("Leave record not found.");

    // calculate total days (exclude cancelled days)
    const totalDays =
      item.leaveDayDetails
        ?.filter((day: any) => !day.isCancelled && !day.IsCancelled)
        ?.reduce((sum: number, day: any) => {
          if (day.dayType === "First-Half" || day.dayType === "Second-Half") return sum + 0.5;
          return sum + 1;
        }, 0) || 0;

    const detailedLeave: LeaveRecordWithDetails = {
  id: item.requestID,
  type: item.leaveName,
  startDate: item.leaveStartDate, // ← must match interface
  endDate: item.leaveEndDate,     // ← must match interface
  days: totalDays,                // you already calculated total days
  reason: item.reason,
  status: item.statusName?.toLowerCase().replace(/\s+/g, "_") || "unknown",
  appliedDate: new Date(item.createdOn).toLocaleDateString(),
  managerName: item.managerName,
  hrName: item.hrName,
  managerResponse: item.managerResponse,
  managerComment: item.managerComment,
  managerResponseOn: item.managerResponseOn,
  hrResponse: item.hrResponse,
  hrComment: item.hrComment,
  hrResponseOn: item.hrResponseOn,
  isCancelled: item.isCancelled,
  leaveDayDetails: (item.leaveDayDetails || []).filter((day: any) => !day.isCancelled && !day.IsCancelled),
  requestID: item.requestID,
};


    setSelectedLeave(detailedLeave);
    setIsReviewModalOpen(true);
  } catch (error) {
    console.error("Error fetching leave details:", error);
    toast({
      title: "Error",
      description: "Failed to fetch leave details.",
      duration: 4000,
      className: "border border-red-300",
    });
  }
};







  const handleCancelClick = (leave: LeaveRecord) => {
    setLeaveToCancelId(leave.id)
    setLeaveToCancel(leave)
    setIsCancelModalOpen(true)
  }

  const handleNotifyClick = (leaveId: number) => {
    toast({
      title: "Success",
      description: "Notified Successfully",
      duration: 3000,
      className: "border border-green-300",
    });
    ////console.log(`Notifying approvers for leave request ID: ${leaveId}`)
  }

  const handleConfirmCancel = async (leaveId: number, cancelledDays?: LeaveDayDetail[]) => {
  setIsLoading(true);
  try {
    // If day-wise cancellation, we need to handle it differently
    // For now, we'll use the same API but you may need to adjust the payload
    // based on your backend requirements
    let payload: any = { requestID: leaveId, modUser: 0 };
    
    // If cancelledDays is provided, it's day-wise cancellation
    if (cancelledDays && cancelledDays.length > 0) {
      // Add cancelled days to payload - API expects leaveDate and dayType
      payload.cancelledDays = cancelledDays.map(day => ({
        leaveDate: day.date,
        dayType: day.type
      }));
    }

    const response = await axios.post(
      process.env.REACT_APP_API_URL +"/api/Leave/CancelLeaveRequest",
      payload,
      { withCredentials: true }
    );

    // Check if response indicates success
    if (response.data && response.data.status === 1) { // assuming status=1 means success
      // Refresh the leave history to get updated data
      await fetchLeaveHistory();
      
      toast({
        title: "Success",
        description: cancelledDays && cancelledDays.length > 0
          ? `${cancelledDays.length} day(s) cancelled successfully.`
          : "Leave request cancelled successfully.",
        duration: 3000,
        className: "border border-green-300",
      });
    } else {
      toast({
        title: "Error",
        description: `Failed to cancel leave request: ${response.data?.message || "Unknown error"}`,
        duration: 4000,
        className: "border border-red-300",
      });
    }
  } catch (error: any) {
    console.error("Error cancelling leave:", error);
    toast({
      title: "Error",
      description: `Error cancelling leave request: ${error?.response?.data?.message || error.message}`,
      duration: 4000,
      className: "border border-red-300",
    });
  } finally {
    setIsCancelModalOpen(false);
    setLeaveToCancelId(null);
    setLeaveToCancel(null);
    setIsLoading(false);
  }
};


  const totalPages = Math.ceil(leaveHistory.length / (pageSize))
  const startIndex = (currentPage - 1) * (pageSize)
  const endIndex = startIndex + (pageSize)
  const currentData = leaveHistory.slice(startIndex, endIndex)

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Leave History</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center text-gray-500 py-8">Loading...</div>
        ) : leaveHistory.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            No leave records found.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50 border-b border-gray-200">
                    <TableHead className="w-12 font-semibold text-gray-700 p-4">
                      #
                    </TableHead>
                    <TableHead className="font-semibold text-gray-700 p-4">
                      Leave Type
                    </TableHead>
                    <TableHead className="font-semibold text-gray-700 p-4">
                      Duration
                    </TableHead>
                    <TableHead className="font-semibold text-gray-700 p-4">
                      Total Days
                    </TableHead>
                    <TableHead className="font-semibold text-gray-700 p-4">
                      Applied On
                    </TableHead>
                    <TableHead className="font-semibold text-gray-700 p-4">
                      Status
                    </TableHead>
                    <TableHead className="w-32 font-semibold text-gray-700 p-4">
                      Actions
                    </TableHead>
                    <TableHead className="w-24 font-semibold text-gray-700 p-4">
                      Notify
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentData.map((leave, index) => (
                    <TableRow
                      key={leave.id}
                      className="hover:bg-gray-50 border-b border-gray-100"
                    >
                      <TableCell>
                        {startIndex + index + 1} <span className="text-xs text-gray-400"></span>
                      </TableCell>

                      <TableCell className="font-medium text-gray-900 p-4">
                        {leave.type}
                      </TableCell>
                      <TableCell className="text-gray-700 p-4">
                        {formatDateToYMD(leave.startDate)} to {formatDateToYMD(leave.endDate)}
                      </TableCell>

                      <TableCell className="text-gray-700 p-4">
                        {leave.days === 0 ? "--" : leave.days}
                      </TableCell>
                      <TableCell className="text-gray-700 p-4">
                        {leave.appliedDate}
                      </TableCell>
                      <TableCell className="p-4">
                        {getStatusBadge(leave.status)}
                      </TableCell>
                      <TableCell className="p-4 flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditLeave(leave)}
                          disabled={
                            new Date(leave.startDate) <= new Date() ||
                            leave.status.toLowerCase() === "cancelled"
                          }
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleViewDetails(leave)}
                          variant="outline"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={
                              leave.status.toLowerCase() === "cancelled" ||
                              leave.status.toLowerCase().includes("rejected") ||
                              new Date(leave.startDate) < new Date() || // disable if start date is past
                              new Date(leave.endDate) < new Date() // disable if end date is past
                            }
                            onClick={() => handleCancelClick(leave)}
                            className="text-gray-700 flex items-center gap-2"
                          >
                            <Ban className="h-4 w-4" />
                            Cancel
                        </Button>
                      </TableCell>
                      <TableCell className="p-4">
                        <Button
                          size="sm"
                          disabled
                          className="bg-gray-400 text-white flex items-center gap-2 font-medium cursor-not-allowed opacity-60"
                        >
                          <BellRing className="h-4 w-4" />
                          Notify
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={leaveHistory.length}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
            />

            {selectedLeave && (
  <LeaveReviewModalEmp
    isOpen={isReviewModalOpen}
    onClose={() => setIsReviewModalOpen(false)}
    leaveRequest={selectedLeave}
    isViewOnly={true} // if you just want to view details
  />
)}


            <CancelLeaveConfirmationModal
              isOpen={isCancelModalOpen}
              onClose={() => {
                setIsCancelModalOpen(false)
                setLeaveToCancel(null)
              }}
              onConfirm={handleConfirmCancel}
              leaveId={leaveToCancelId}
              leaveDayDetails={
                leaveToCancel?.leaveDayDetails && leaveToCancel.leaveDayDetails.length > 0
                  ? leaveToCancel.leaveDayDetails.filter((day: any) => !day.isCancelled && !day.IsCancelled) // Only show non-cancelled days
                  : leaveToCancel
                    ? generateLeaveDayDetails(leaveToCancel.startDate, leaveToCancel.endDate, leaveToCancel.days)
                    : []
              }
            />
          </>
        )}
        <Dialog open={isAddLeaveModalOpen} onOpenChange={setIsAddLeaveModalOpen}>
          <DialogContent className="sm:max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{leaveToEdit ? "Edit Leave" : "New Leave Request"}</DialogTitle>
            </DialogHeader>
            <AddLeaveForm
              defaultValues={
                leaveToEdit
                  ? {
                    requestID: leaveToEdit.requestID,
                      leaveType: leaveToEdit.type, // rename 'type' → 'leaveType'
                      startDate: leaveToEdit.startDate,
                      endDate: leaveToEdit.endDate,
                      reason: leaveToEdit.reason,
                      leaveDayDetails: (leaveToEdit.leaveDayDetails || []).filter((day: any) => !day.isCancelled && !day.IsCancelled),
                    }
                  : undefined
              }
                onSubmitSuccess={() => {
                  setIsAddLeaveModalOpen(false); // ✅ Close the modal
                  fetchLeaveHistory();           // Refresh the table
                }}

            />
          </DialogContent>
        </Dialog>


        {isLoading && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
            <div className="w-16 h-16 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
          </div>
        )}

      </CardContent>
    </Card>
  )
}
